import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Grid, Chip, Snackbar, Alert, Tabs, Tab,
  CircularProgress, Tooltip, Divider, Autocomplete, Switch, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Refresh as RefreshIcon, LockOpen as UnlockIcon,
  Person as PersonIcon, School as SchoolIcon,
  SupervisorAccount as AdminIcon, People as ParentIcon, Face as StudentIcon,
  LinkOff as UnlinkIcon, PersonAdd as PersonAddIcon, Link as LinkIcon,
  Star as StarIcon, StarBorder as StarBorderIcon
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';

const EMPTY_FORM = {
  email: '', password: '', first_name: '', last_name: '',
  phone: '', address: '', city: '', state: '', zip: '',
  status: 'active', role: 'student'
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy] = useState('last_name');
  const [sortDirection] = useState('ASC');
  const [tabValue, setTabValue] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Parent-student linking state
  const [linkedParents, setLinkedParents] = useState([]);
  const [parentsLoading, setParentsLoading] = useState(false);
  const [linkDialog, setLinkDialog] = useState(false);
  const [allParents, setAllParents] = useState([]);
  const [allParentsLoading, setAllParentsLoading] = useState(false);
  const [linkForm, setLinkForm] = useState({ parentUserId: null, relationshipType: 'Guardian', isPrimary: false });
  const [createLinkDialog, setCreateLinkDialog] = useState(false);
  const [createLinkForm, setCreateLinkForm] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '', relationshipType: 'Guardian', isPrimary: false });
  const [createLinkLoading, setCreateLinkLoading] = useState(false);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.getUsers({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        role: roleFilter,
        status: statusFilter,
        search: searchQuery,
        sortBy,
        sortDirection
      });
      setUsers(result.users || []);
      setTotalUsers(result.total || 0);
    } catch (err) {
      showSnackbar('Failed to load users: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, roleFilter, statusFilter, searchQuery, sortBy, sortDirection]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    const roles = ['', 'admin', 'teacher', 'parent', 'student'];
    setRoleFilter(roles[newValue] || '');
    setPage(0);
  };

  const handleSearch = () => { setPage(0); fetchUsers(); };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPage(0);
  };

  const loadLinkedParents = async (studentUserId) => {
    if (!studentUserId) return;
    setParentsLoading(true);
    try {
      const data = await apiClient.getStudentParents(studentUserId);
      setLinkedParents(data.parents || []);
    } catch (err) {
      showSnackbar('Failed to load linked parents: ' + err.message, 'error');
    } finally {
      setParentsLoading(false);
    }
  };

  const handleOpenLinkDialog = async () => {
    setLinkForm({ parentUserId: null, relationshipType: 'Guardian', isPrimary: false });
    setLinkDialog(true);
    setAllParentsLoading(true);
    try {
      const data = await apiClient.getUsers({ role: 'parent', limit: 200 });
      setAllParents(data.users || []);
    } catch (err) {
      showSnackbar('Failed to load parent users: ' + err.message, 'error');
    } finally {
      setAllParentsLoading(false);
    }
  };

  const handleLinkExistingParent = async () => {
    if (!linkForm.parentUserId) { showSnackbar('Please select a parent', 'error'); return; }
    try {
      const data = await apiClient.linkParentToStudent(currentUser.user_id, {
        parentUserId: linkForm.parentUserId,
        relationshipType: linkForm.relationshipType,
        isPrimary: linkForm.isPrimary
      });
      setLinkedParents(data.parents || []);
      setLinkDialog(false);
      showSnackbar('Parent linked successfully');
    } catch (err) {
      showSnackbar('Failed to link parent: ' + err.message, 'error');
    }
  };

  const handleCreateAndLinkParent = async () => {
    if (!createLinkForm.first_name || !createLinkForm.last_name || !createLinkForm.email || !createLinkForm.password) {
      showSnackbar('First name, last name, email, and password are required', 'error');
      return;
    }
    setCreateLinkLoading(true);
    try {
      const { relationshipType, isPrimary, ...parentFields } = createLinkForm;
      const data = await apiClient.linkParentToStudent(currentUser.user_id, {
        createParent: parentFields,
        relationshipType,
        isPrimary
      });
      setLinkedParents(data.parents || []);
      setCreateLinkDialog(false);
      setCreateLinkForm({ first_name: '', last_name: '', email: '', password: '', phone: '', relationshipType: 'Guardian', isPrimary: false });
      showSnackbar('Parent account created and linked');
    } catch (err) {
      showSnackbar('Failed to create parent: ' + err.message, 'error');
    } finally {
      setCreateLinkLoading(false);
    }
  };

  const handleUnlinkParent = async (parentUserId, parentName) => {
    try {
      await apiClient.unlinkParentFromStudent(currentUser.user_id, parentUserId);
      setLinkedParents(prev => prev.filter(p => p.parent_user_id !== parentUserId));
      showSnackbar(`${parentName} unlinked`);
    } catch (err) {
      showSnackbar('Failed to unlink parent: ' + err.message, 'error');
    }
  };

  const handleOpenCreate = () => {
    setDialogMode('create');
    setCurrentUser(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (userId) => {
    setFormLoading(true);
    setDialogOpen(true);
    setDialogMode('edit');
    setLinkedParents([]);
    try {
      const user = await apiClient.getUserById(userId);
      setCurrentUser(user);
      const role = (user.roles && user.roles[0]) || 'student';
      setFormData({
        email: user.email || '',
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip: user.zip || '',
        status: user.status || 'active',
        role,
        teacherData: user.teacherData || {},
        parentData: user.parentData || {},
        studentData: user.studentData || {}
      });
      if (role === 'student') loadLinkedParents(userId);
    } catch (err) {
      showSnackbar('Failed to load user: ' + err.message, 'error');
      setDialogOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      showSnackbar('First name, last name, and email are required.', 'error');
      return;
    }
    if (dialogMode === 'create' && !formData.password) {
      showSnackbar('Password is required when creating a user.', 'error');
      return;
    }
    setFormLoading(true);
    try {
      if (dialogMode === 'create') {
        await apiClient.registerUser(formData, formData.role);
        showSnackbar(`${formData.first_name} ${formData.last_name} created successfully`);
      } else {
        await apiClient.updateUser(currentUser.user_id, formData);
        showSnackbar(`${formData.first_name} ${formData.last_name} updated successfully`);
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      showSnackbar(`Failed to ${dialogMode === 'create' ? 'create' : 'update'} user: ${err.message}`, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    setLoading(true);
    try {
      await apiClient.deleteUser(userToDelete.user_id);
      showSnackbar(`${userToDelete.first_name} ${userToDelete.last_name} deleted`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      showSnackbar('Failed to delete user: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (user) => {
    try {
      await apiClient.unlockUser(user.user_id);
      showSnackbar(`${user.first_name} ${user.last_name}'s account unlocked`);
      fetchUsers();
    } catch (err) {
      showSnackbar('Failed to unlock account: ' + err.message, 'error');
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNestedChange = (e) => {
    const { name, value } = e.target;
    const [section, field] = name.split('.');
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const getRoleColor = (role) => (
    { admin: 'error', teacher: 'primary', parent: 'success', student: 'info' }[role] || 'default'
  );
  const getStatusColor = (status) => (
    { active: 'success', pending: 'warning', inactive: 'error' }[status] || 'default'
  );
  const getRoleIcon = (role) => (
    { admin: <AdminIcon />, teacher: <SchoolIcon />, parent: <ParentIcon />, student: <StudentIcon /> }[role] || <PersonIcon />
  );

  const renderRoleFields = () => {
    if (formData.role === 'teacher') return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Teacher Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Title" name="teacherData.title"
              value={formData.teacherData?.title || ''} onChange={handleNestedChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Subjects" name="teacherData.subjects"
              value={formData.teacherData?.subjects || ''} onChange={handleNestedChange}
              helperText="Separate subjects with commas" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Bio" name="teacherData.bio" multiline rows={3}
              value={formData.teacherData?.bio || ''} onChange={handleNestedChange} />
          </Grid>
        </Grid>
      </Box>
    );

    if (formData.role === 'parent') return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Parent Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Emergency Contact Name" name="parentData.emergency_contact_name"
              value={formData.parentData?.emergency_contact_name || ''} onChange={handleNestedChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Emergency Contact Phone" name="parentData.emergency_contact_phone"
              value={formData.parentData?.emergency_contact_phone || ''} onChange={handleNestedChange} />
          </Grid>
        </Grid>
      </Box>
    );

    if (formData.role === 'student') return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Student Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Date of Birth" name="studentData.date_of_birth" type="date"
              value={formData.studentData?.date_of_birth || ''} onChange={handleNestedChange}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Current Grade</InputLabel>
              <Select name="studentData.current_grade" label="Current Grade"
                value={formData.studentData?.current_grade || ''} onChange={handleNestedChange}>
                <MenuItem value="K">Kindergarten</MenuItem>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(g => (
                  <MenuItem key={g} value={String(g)}>{g === 1 ? '1st' : g === 2 ? '2nd' : g === 3 ? '3rd' : `${g}th`} Grade</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Emergency Contact Name" name="studentData.emergency_contact_name"
              value={formData.studentData?.emergency_contact_name || ''} onChange={handleNestedChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Emergency Contact Phone" name="studentData.emergency_contact_phone"
              value={formData.studentData?.emergency_contact_phone || ''} onChange={handleNestedChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Medical Information" name="studentData.medical_info" multiline rows={2}
              value={formData.studentData?.medical_info || ''} onChange={handleNestedChange} />
          </Grid>
        </Grid>

        {/* Parents / Guardians panel — only shown in edit mode */}
        {dialogMode === 'edit' && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Parents / Guardians</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={handleOpenLinkDialog}>
                  Link Existing
                </Button>
                <Button size="small" variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateLinkDialog(true)}>
                  Create & Link
                </Button>
              </Box>
            </Box>
            {parentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : linkedParents.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontStyle: 'italic' }}>
                No parents linked. Use the buttons above to link or create a parent.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Relationship</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Primary</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkedParents.map(p => (
                    <TableRow key={p.parent_user_id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{p.first_name} {p.last_name}</TableCell>
                      <TableCell>{p.email}</TableCell>
                      <TableCell>{p.relationship_type}</TableCell>
                      <TableCell>
                        {p.is_primary
                          ? <Chip icon={<StarIcon />} label="Primary" color="primary" size="small" />
                          : <StarBorderIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                        }
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Unlink">
                          <IconButton size="small" color="error"
                            onClick={() => handleUnlinkParent(p.parent_user_id, `${p.first_name} ${p.last_name}`)}>
                            <UnlinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </Box>
    );

    return null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>User Management</Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
          <Tab icon={<PersonIcon />} label="All Users" iconPosition="start" />
          <Tab icon={<AdminIcon />} label="Admins" iconPosition="start" />
          <Tab icon={<SchoolIcon />} label="Teachers" iconPosition="start" />
          <Tab icon={<ParentIcon />} label="Parents" iconPosition="start" />
          <Tab icon={<StudentIcon />} label="Students" iconPosition="start" />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={4}>
            <TextField fullWidth label="Search Users" value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{ endAdornment: <IconButton onClick={handleSearch}><SearchIcon /></IconButton> }} />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button fullWidth variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>Reset</Button>
          </Grid>
          <Grid item xs={6} sm={2} md={4}>
            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Add User
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No users found
                  </TableCell>
                </TableRow>
              ) : users.map(user => (
                <TableRow key={user.user_id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{user.first_name} {user.last_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {(user.roles || []).map(role => (
                      <Chip key={role} icon={getRoleIcon(role)}
                        label={role.charAt(0).toUpperCase() + role.slice(1)}
                        color={getRoleColor(role)} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip label={user.status?.charAt(0).toUpperCase() + user.status?.slice(1)}
                      color={getStatusColor(user.status)} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Unlock Account">
                      <IconButton size="small" color="warning" onClick={() => handleUnlock(user)}>
                        <UnlockIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(user.user_id)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => { setUserToDelete(user); setDeleteDialogOpen(true); }}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'Add New User' : 'Edit User'}</DialogTitle>
        <DialogContent dividers>
          {formLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Basic Information</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="First Name" name="first_name"
                  value={formData.first_name} onChange={handleFieldChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Last Name" name="last_name"
                  value={formData.last_name} onChange={handleFieldChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Email" name="email" type="email"
                  value={formData.email} onChange={handleFieldChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Password" name="password" type="password"
                  value={formData.password} onChange={handleFieldChange}
                  required={dialogMode === 'create'}
                  helperText={dialogMode === 'edit' ? 'Leave blank to keep current password' : 'Minimum 8 characters'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" name="phone"
                  value={formData.phone} onChange={handleFieldChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select name="role" label="Role" value={formData.role} onChange={handleFieldChange}
                    disabled={dialogMode === 'edit'}>
                    <MenuItem value="admin">Administrator</MenuItem>
                    <MenuItem value="teacher">Teacher</MenuItem>
                    <MenuItem value="parent">Parent</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select name="status" label="Status" value={formData.status} onChange={handleFieldChange}>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>Address</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Street Address" name="address"
                  value={formData.address} onChange={handleFieldChange} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth label="City" name="city"
                  value={formData.city} onChange={handleFieldChange} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="State" name="state"
                  value={formData.state} onChange={handleFieldChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="ZIP" name="zip"
                  value={formData.zip} onChange={handleFieldChange} />
              </Grid>

              <Grid item xs={12}>{renderRoleFields()}</Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={formLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={18} color="inherit" /> : null}>
            {dialogMode === 'create' ? 'Create User' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Existing Parent Dialog */}
      <Dialog open={linkDialog} onClose={() => setLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Existing Parent</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {allParentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : (
            <>
              <Autocomplete
                options={allParents}
                getOptionLabel={o => `${o.first_name} ${o.last_name} (${o.email})`}
                onChange={(_, val) => setLinkForm(p => ({ ...p, parentUserId: val?.user_id || null }))}
                renderInput={params => <TextField {...params} label="Search parents" fullWidth />}
                noOptionsText="No parent accounts found"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Relationship</InputLabel>
                <Select value={linkForm.relationshipType} label="Relationship"
                  onChange={e => setLinkForm(p => ({ ...p, relationshipType: e.target.value }))}>
                  {['Father','Mother','Stepfather','Stepmother','Guardian','Grandparent','Other'].map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch checked={linkForm.isPrimary} onChange={e => setLinkForm(p => ({ ...p, isPrimary: e.target.checked }))} />}
                label="Primary Guardian"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLinkExistingParent} disabled={!linkForm.parentUserId}>
            Link Parent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create & Link New Parent Dialog */}
      <Dialog open={createLinkDialog} onClose={() => setCreateLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Parent Account</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="First Name" value={createLinkForm.first_name}
                onChange={e => setCreateLinkForm(p => ({ ...p, first_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Last Name" value={createLinkForm.last_name}
                onChange={e => setCreateLinkForm(p => ({ ...p, last_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Email" type="email" value={createLinkForm.email}
                onChange={e => setCreateLinkForm(p => ({ ...p, email: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Password" type="password" value={createLinkForm.password}
                onChange={e => setCreateLinkForm(p => ({ ...p, password: e.target.value }))}
                helperText="Minimum 8 characters" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" value={createLinkForm.phone}
                onChange={e => setCreateLinkForm(p => ({ ...p, phone: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Relationship</InputLabel>
                <Select value={createLinkForm.relationshipType} label="Relationship"
                  onChange={e => setCreateLinkForm(p => ({ ...p, relationshipType: e.target.value }))}>
                  {['Father','Mother','Stepfather','Stepmother','Guardian','Grandparent','Other'].map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={createLinkForm.isPrimary} onChange={e => setCreateLinkForm(p => ({ ...p, isPrimary: e.target.checked }))} />}
                label="Primary Guardian"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLinkDialog(false)} disabled={createLinkLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateAndLinkParent} disabled={createLinkLoading}
            startIcon={createLinkLoading ? <CircularProgress size={18} color="inherit" /> : null}>
            Create & Link
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
